import { Grid, Paper, TablePagination } from '@mui/material';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import MDButton from '@components/MDButton';
import { useMaterialUIController } from 'context';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CompanyService } from './services/company.service';
import { CompanyResponse } from './services/responses/company.response';
import { CompanyHook } from './hooks/company.hook';
import { CompaniesFilter } from './components/companies.filter';
import { CompaniesTable } from './components/companies.table';
import { CompaniesModal } from './components/companies.modal';
import { CompanyDetailModal } from './components/company-detail.modal';

export const Companies = () => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const { sidenavColor } = controller;
    const service = new CompanyService();

    // ?empresa={id} abre o detalhe direto: é por onde a tela do lead chega.
    const [searchParams] = useSearchParams();

    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [search, setSearch] = useState('');
    const [filterFinal, setFilterFinal] = useState('');
    const [refresh, setRefresh] = useState(true);
    const [total, setTotal] = useState(0);
    const [openModal, setOpenModal] = useState(false);
    const [editItem, setEditItem] = useState<CompanyResponse | null>(null);
    const [detailId, setDetailId] = useState<string | null>(searchParams.get('empresa'));

    const { json } = new CompanyHook({
        page,
        limit: rowsPerPage,
        orderBy: 'Name',
        orderDirection: 'asc',
        filter: filterFinal,
        refresh,
        service,
    }).getJson();

    useEffect(() => {
        setTotal(json?.data?.totalItens ?? 0);
    }, [json]);

    const applySearch = () => {
        setPage(0);
        setFilterFinal(search ? `?search=${encodeURIComponent(search)}` : '');
    };

    const clear = () => {
        setSearch('');
        setFilterFinal('');
        setRefresh((p) => !p);
    };

    const getAll = () => setRefresh((p) => !p);

    const handleModal = () => {
        if (openModal) setEditItem(null);
        setOpenModal((p) => !p);
    };

    const getById = async (id: string) => {
        const res = await service.getById(id);
        setEditItem(res.data.data);
        setDetailId(null);
        setOpenModal(true);
    };

    return (
        <DashboardLayout>
            <DashboardNavbar title={t('companies.titulo')} />

            <Grid container spacing={1}>
                <CompaniesFilter
                    search={search}
                    setSearch={setSearch}
                    onSearch={applySearch}
                    onClear={clear}
                    onEnter={(e) => e.key === 'Enter' && applySearch()}
                />
                <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                    <MDButton variant="gradient" color={sidenavColor} onClick={handleModal} sx={{ height: 40, minWidth: 130, whiteSpace: 'nowrap' }}>
                        {t('general.adicionar')}
                    </MDButton>
                </Grid>
            </Grid>

            <Paper sx={{ width: '100%', overflow: 'hidden', mt: 2 }}>
                <CompaniesTable
                    data={json}
                    editAction={getById}
                    refresh={refresh}
                    setRefresh={setRefresh}
                />
                <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    labelRowsPerPage={t('general.linhasPorPagina')}
                    labelDisplayedRows={({ from, to, count }) =>
                        `${from}-${to} ${t('general.X-de-X')} ${count !== -1 ? count : `${t('general.maisQue')} ${to}`}`
                    }
                    component="div"
                    count={total}
                    page={page}
                    onPageChange={(_, p) => setPage(p)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                    }}
                />
            </Paper>

            <CompaniesModal open={openModal} edit={editItem} handleModal={handleModal} getAll={getAll} />

            <CompanyDetailModal companyId={detailId} onClose={() => setDetailId(null)} onEdit={getById} />
        </DashboardLayout>
    );
};
